import React, { createContext, useContext, useRef, useCallback, useEffect } from 'react';
import { useSettings } from './SettingsContext';
import { unlockAudio } from '../utils/alertSound';
import { hasSpeechSynthesis } from '../utils/deviceCapability';

const VoiceContext = createContext(null);

/**
 * 语音通道定义
 *
 *  announce —— 到点提醒播报
 *      由系统在用药时间【主动】触发，内容固定为「药品名称 + 剂量」。
 *      这类播报是产品的核心价值：老人可能没在看屏幕，必须靠声音知道该吃什么药。
 *      它属于「提醒方式」的一部分 —— 只有选到含语音的提醒方式时才播报。
 *
 *  ui —— 界面操作播报
 *      只在用户【点击操作之后】触发，内容是操作结果反馈（如「已切换到温暖橙主题」）。
 *      关掉它不影响吃药提醒，只是界面变安静。
 */
function isChannelEnabled(type, settings) {
  if (type === 'announce') {
    const mode = settings.reminder_mode || 'all';
    return mode === 'all' || mode === 'voice';
  }
  return !!settings.voice_ui;
}

/**
 * 挑一个中文语音。
 *
 * 手机上的语音列表是异步加载的，首次调用常常拿到空数组。
 * 这时返回 null 交给系统默认语音，而不是随便塞一个英文音色去念中文。
 */
function pickChineseVoice() {
  try {
    const voices = window.speechSynthesis.getVoices() || [];
    if (!voices.length) return null;
    const norm = v => String(v.lang || '').replace('_', '-').toLowerCase();
    return (
      voices.find(v => norm(v) === 'zh-cn') ||
      voices.find(v => norm(v).startsWith('zh')) ||
      null
    );
  } catch (e) {
    return null;
  }
}

export function VoiceProvider({ children }) {
  const { settings } = useSettings();
  const queueRef = useRef([]);
  const speakingRef = useRef(false);

  /**
   * 语音引擎预热 + 音频上下文解锁。
   *
   * iPhone 的 Safari 必须「先被用户碰一下」才允许网页发声，
   * 所以这里在首次交互时做两件事：播一个静音 utterance 解锁语音引擎，
   * 同时唤醒音频上下文（提示铃声也依赖它）。
   * 注意这个动作必须直接在事件回调里同步执行，延迟执行会失去解锁资格。
   */
  useEffect(() => {
    if (!hasSpeechSynthesis()) return;
    const synth = window.speechSynthesis;

    const refreshVoices = () => {
      try {
        synth.getVoices();
      } catch (e) {
        // 忽略
      }
    };
    refreshVoices();

    const unlock = () => {
      refreshVoices();
      unlockAudio();
      try {
        const silent = new SpeechSynthesisUtterance(' ');
        silent.volume = 0;
        synth.speak(silent);
      } catch (e) {
        // 忽略
      }
      window.removeEventListener('touchstart', unlock);
      window.removeEventListener('click', unlock);
    };

    synth.onvoiceschanged = refreshVoices;
    window.addEventListener('touchstart', unlock);
    window.addEventListener('click', unlock);
    return () => {
      synth.onvoiceschanged = null;
      window.removeEventListener('touchstart', unlock);
      window.removeEventListener('click', unlock);
    };
  }, []);

  const playNext = useCallback(() => {
    if (speakingRef.current || queueRef.current.length === 0) return;
    const { text, onEnd } = queueRef.current.shift();
    speakingRef.current = true;
    try {
      const synth = window.speechSynthesis;
      const utterance = new SpeechSynthesisUtterance(text);
      utterance.lang = 'zh-CN';
      utterance.rate = parseFloat(settings.voice_speed) || 1;
      utterance.pitch = 1.0;
      utterance.volume = 1.0;
      const zhVoice = pickChineseVoice();
      if (zhVoice) utterance.voice = zhVoice;
      utterance.onend = () => {
        speakingRef.current = false;
        if (onEnd) onEnd();
        playNext();
      };
      utterance.onerror = () => {
        speakingRef.current = false;
        if (onEnd) onEnd();
        playNext();
      };
      synth.speak(utterance);
    } catch (e) {
      speakingRef.current = false;
      if (onEnd) onEnd();
      playNext();
    }
  }, [settings.voice_speed]);

  /**
   * 播报一段文字。
   *
   * @param {string} text            要播报的内容
   * @param {object} options
   * @param {'ui'|'announce'} options.type  走哪条通道，决定受哪个设置控制，默认 'ui'
   * @param {boolean} options.force  忽略设置强制播报。仅用于「测试语音」这类设备自检场景，
   *                                 让用户在各项开关都关掉时也能确认设备音量是否正常。
   * @param {Function} options.onEnd 播报结束（或被跳过）后的回调
   */
  const speak = useCallback((text, options = {}) => {
    const { type = 'ui', force = false, onEnd } = options;

    if (!force && !isChannelEnabled(type, settings)) {
      if (onEnd) onEnd();
      return;
    }
    if (!hasSpeechSynthesis()) {
      if (onEnd) onEnd();
      return;
    }

    const synth = window.speechSynthesis;
    // 只在确实有内容正在播时才打断。
    // 一是无谓的 cancel 会让部分浏览器吞掉紧随其后的 speak；
    // 二是原来这里统一「延迟 100ms 再发声」，那会让点击播报脱离用户手势的调用栈，
    // 移动端浏览器（尤其 iPhone）会直接拒绝发声 —— 表现就是点了按钮一点声音都没有。
    if (speakingRef.current || queueRef.current.length > 0) {
      try {
        synth.cancel();
      } catch (e) {
        // 忽略
      }
      queueRef.current = [];
      speakingRef.current = false;
    }

    queueRef.current.push({ text, onEnd });
    playNext();
  }, [settings, playNext]);

  const stop = useCallback(() => {
    if (hasSpeechSynthesis()) {
      try {
        window.speechSynthesis.cancel();
      } catch (e) {
        // 忽略
      }
    }
    queueRef.current = [];
    speakingRef.current = false;
  }, []);

  return (
    <VoiceContext.Provider value={{ speak, stop }}>
      {children}
    </VoiceContext.Provider>
  );
}

export function useVoice() {
  const ctx = useContext(VoiceContext);
  if (!ctx) throw new Error('useVoice must be used within VoiceProvider');
  return ctx;
}
